import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileBarChart, Download, FileText, TrendingUp, Calendar } from 'lucide-react';
import { districts, sites, monthlyGeneration } from '../mock';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Reports = () => {
  const [reportType, setReportType] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('july');

  const reportTypes = [
    { id: 'daily-gen', name: 'Daily Generation Report', icon: TrendingUp, description: 'Day-wise generation data with site-level breakdown' },
    { id: 'monthly-perf', name: 'Monthly Performance Report', icon: Calendar, description: 'Monthly PR, CUF and generation analysis' },
    { id: 'pr-cuf', name: 'PR & CUF Consolidated Report', icon: FileBarChart, description: 'Performance ratio and capacity utilization metrics' },
    { id: 'carbon', name: 'Carbon Credit Report', icon: FileText, description: 'CO2 reduction and carbon credit computation' },
    { id: 'complaint', name: 'Complaint Resolution Report', icon: FileText, description: 'Service ticket analysis and resolution metrics' },
    { id: 'downtime', name: 'Device Downtime Report', icon: FileText, description: 'Device unavailability and impact analysis' },
  ];

  const handleGenerateReport = () => {
    console.log('Generating report:', reportType, selectedDistrict, selectedMonth);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>MIS Reports</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Generate comprehensive management information system reports</p>
      </div>

      {/* Report Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportTypes.map((report) => (
          <Card
            key={report.id}
            className={`cursor-pointer hover:shadow-lg transition-all ${
              reportType === report.id ? 'ring-2' : ''
            }`}
            style={{
              ringColor: reportType === report.id ? '#10ac84' : 'transparent',
            }}
            onClick={() => setReportType(report.id)}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                  <report.icon className="w-6 h-6" style={{ color: '#10ac84' }} />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base mb-2">{report.name}</CardTitle>
                  <p className="text-sm" style={{ color: '#576574' }}>{report.description}</p>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" style={{ color: '#3498db' }} />
              Generation Trend (2025)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Line
                data={{
                  labels: monthlyGeneration.map(month => month.month),
                  datasets: [{
                    label: 'Generation (kWh)',
                    data: monthlyGeneration.map(month => month.generation),
                    borderColor: '#3498db',
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    tension: 0.4,
                    fill: true,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                      callbacks: {
                        label: function(context) {
                          return context.parsed.y.toLocaleString() + ' kWh';
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                        callback: function(value) {
                          return (value / 1000).toFixed(0) + 'k';
                        }
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileBarChart className="w-5 h-5" style={{ color: '#9b59b6' }} />
              District Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: districts.map(d => d.name),
                  datasets: [
                    {
                      label: 'PR (%)',
                      data: districts.map(d => d.pr),
                      backgroundColor: 'rgba(243, 156, 18, 0.8)',
                      borderColor: '#f39c12',
                      borderWidth: 1,
                    },
                    {
                      label: 'CUF (%)',
                      data: districts.map(d => d.cuf),
                      backgroundColor: 'rgba(155, 89, 182, 0.8)',
                      borderColor: '#9b59b6',
                      borderWidth: 1,
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Configuration */}
      {reportType && (
        <Card>
          <CardHeader>
            <CardTitle>Report Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>District</Label>
                  <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select district" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Districts</SelectItem>
                      {districts.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="january">January 2025</SelectItem>
                      <SelectItem value="february">February 2025</SelectItem>
                      <SelectItem value="march">March 2025</SelectItem>
                      <SelectItem value="april">April 2025</SelectItem>
                      <SelectItem value="may">May 2025</SelectItem>
                      <SelectItem value="june">June 2025</SelectItem>
                      <SelectItem value="july">July 2025</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Format</Label>
                  <Select defaultValue="pdf">
                    <SelectTrigger>
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="both">Both PDF & Excel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleGenerateReport}
                  className="flex-1"
                  style={{ backgroundColor: '#10ac84' }}
                >
                  <FileBarChart className="w-4 h-4 mr-2" />
                  Generate Report
                </Button>
                <Button variant="outline" className="flex-1">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sample Report Preview */}
      {reportType === 'monthly-perf' && (
        <Card>
          <CardHeader>
            <CardTitle>Report Preview - Monthly Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                <div className="text-sm font-semibold mb-3" style={{ color: '#0a3d62' }}>Summary</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs" style={{ color: '#576574' }}>Total Generation</div>
                    <div className="text-lg font-bold" style={{ color: '#0a3d62' }}>48,000 kWh</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: '#576574' }}>Average PR</div>
                    <div className="text-lg font-bold" style={{ color: '#f39c12' }}>68.2%</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: '#576574' }}>Average CUF</div>
                    <div className="text-lg font-bold" style={{ color: '#9b59b6' }}>17.2%</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: '#576574' }}>Active Sites</div>
                    <div className="text-lg font-bold" style={{ color: '#10ac84' }}>41/43</div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b" style={{ backgroundColor: '#f5f7fa' }}>
                      <th className="text-left p-3 font-semibold text-sm" style={{ color: '#576574' }}>Month</th>
                      <th className="text-left p-3 font-semibold text-sm" style={{ color: '#576574' }}>Generation (kWh)</th>
                      <th className="text-left p-3 font-semibold text-sm" style={{ color: '#576574' }}>Capacity (MW)</th>
                      <th className="text-left p-3 font-semibold text-sm" style={{ color: '#576574' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyGeneration.map((month, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3">{month.month}</td>
                        <td className="p-3 font-semibold">{month.generation.toLocaleString()}</td>
                        <td className="p-3">{month.capacity} MW</td>
                        <td className="p-3">
                          <Badge variant="outline" style={{ backgroundColor: '#e8f5f1', color: '#10ac84' }}>
                            On Target
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Reports */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { name: 'Monthly Performance Report - June 2025', date: '2025-07-01', size: '2.3 MB', format: 'PDF' },
              { name: 'PR & CUF Consolidated - Q2 2025', date: '2025-06-30', size: '1.8 MB', format: 'Excel' },
              { name: 'Carbon Credit Report - May 2025', date: '2025-06-15', size: '980 KB', format: 'PDF' },
            ].map((report, idx) => (
              <div key={idx} className="p-4 rounded-lg border flex items-center justify-between hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                    <FileText className="w-5 h-5" style={{ color: '#10ac84' }} />
                  </div>
                  <div>
                    <div className="font-semibold" style={{ color: '#0a3d62' }}>{report.name}</div>
                    <div className="text-sm" style={{ color: '#576574' }}>
                      Generated: {report.date} • {report.size} • {report.format}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
